import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import {
  MessageCircle,
  X,
  Send,
  Minimize2,
  Maximize2,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  AlertCircle,
  ExternalLink,
  Home,
} from "lucide-react";
import { knowledgeBaseService } from "../../apis/knowledgeBaseService";

// ---------- Memoized Input Component ----------
const ChatInput = memo(({ onSend, onReset, isLoading }) => {
  const [value, setValue] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const trimmed = value.trim();
      if (trimmed === "0") {
        onReset();
        setValue("");
      } else if (trimmed) {
        onSend(trimmed);
        setValue("");
      }
    }
  };

  return (
    <div className="p-2 border-t border-gray-200 bg-white flex-shrink-0">
      <div className="flex items-center gap-1.5">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Ask a question... (or 0 + Enter to reset)"
          rows={1}
          className="flex-1 resize-none rounded border border-gray-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          style={{ maxHeight: "4rem" }}
          autoFocus
        />
        <button
          onClick={() => {
            const trimmed = value.trim();
            if (trimmed === "0") {
              onReset();
              setValue("");
            } else if (trimmed) {
              onSend(trimmed);
              setValue("");
            }
          }}
          disabled={!value.trim() || isLoading}
          className="bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded p-1.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          <Send size={14} />
        </button>
      </div>
      <p className="text-xs text-gray-400 text-center mt-1">
        ⚠️ Platform help only — don’t enter patient information.
      </p>
      <p className="text-xs text-gray-400 text-center mt-0.5">
        💡 Type <kbd>0</kbd> + Enter to reset chat
      </p>
    </div>
  );
});

// ---------- Main ChatBot Component ----------
const ChatBot = ({ isVisible = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const staticSuggestedQuestions = [
    "How do I start a recording?",
    "How do I generate a SOAP note?",
    "How do I sync a note to eClinicalWorks?",
    "Why didn't my note sync to eCW?",
    "What languages does Virtualscribe support?",
    "Is Virtualscribe HIPAA compliant?",
    "My microphone isn't working",
  ];

  const resetChat = useCallback(() => {
    setMessages([]);
  }, []);

  const sendMessage = useCallback(
    async (message) => {
      if (!message.trim()) return;

      const userMessage = {
        role: "user",
        content: message,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      const answer = await knowledgeBaseService.query(message);
      const botMessage = {
        role: "assistant",
        content: answer,
        timestamp: Date.now(),
        messageId: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        usedFallback:
          answer.includes("Help Center") || answer.includes("support ticket"),
      };
      setMessages((prev) => [...prev, botMessage]);
      setIsLoading(false);
    },
    []
  );

  const handleFeedback = useCallback(async (messageId, isHelpful) => {
    await knowledgeBaseService.submitFeedback(messageId, isHelpful);
    setMessages((prev) =>
      prev.map((msg) =>
        msg.messageId === messageId ? { ...msg, feedbackGiven: true } : msg
      )
    );
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!isVisible) return null;

  if (!isOpen) {
    return (
      <button
        onClick={() => {
          setIsOpen(true);
          setIsMinimized(false);
        }}
        className="tour-hide-on-mobile fixed bottom-4 right-4 z-50 bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-xl text-white rounded-full shadow-lg transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center"
        style={{ width: "56px", height: "56px" }}
        aria-label="Open VirtualScribe Assistant"
      >
        <MessageCircle size={28} />
      </button>
    );
  }

  const Header = () => (
    <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white flex-shrink-0">
      <div className="flex items-center gap-1.5">
        <MessageCircle size={14} />
        <h2 className="font-semibold text-sm">Virtualscribe Assistant</h2>
      </div>
      <div className="flex items-center gap-0.5">
        <button
          onClick={() => setIsMinimized(!isMinimized)}
          className="p-1 hover:bg-white/20 rounded transition-colors"
          title={isMinimized ? "Expand" : "Minimize"}
        >
          {isMinimized ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
        </button>
        <button
          onClick={() => setIsOpen(false)}
          className="p-1 hover:bg-white/20 rounded transition-colors"
          title="Close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );

  const ChatBody = () => (
    <>
      <div className="flex-1 min-h-[80px] overflow-y-auto p-3 space-y-2 bg-gray-50">
        {messages.length === 0 ? (
          <div className="text-center space-y-2">
            <p className="text-xs text-gray-700 font-medium">
              Hi! Ask me how to use Virtualscribe. Try one of these:
            </p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {staticSuggestedQuestions.map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => sendMessage(q)}
                  className="text-xs bg-white border border-gray-200 rounded-full px-2.5 py-1 text-gray-700 hover:bg-blue-50 hover:border-blue-200 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
            <div className="text-xs text-gray-400 bg-yellow-50 p-1.5 rounded-lg mt-1.5">
              <AlertCircle size={10} className="inline mr-0.5" />
              Platform help only —{" "}
              <strong>don’t enter patient information.</strong>
            </div>
            <a
              href="/support-ticket"
              className="text-xs text-blue-600 hover:underline inline-flex items-center gap-0.5 mt-1"
            >
              <HelpCircle size={10} /> Can’t find an answer? Open a support
              ticket
            </a>
            <div className="text-xs text-gray-400 mt-1.5">
              💡 Tip: Type <kbd>0</kbd> and press Enter to go back to home.
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs ${
                    msg.role === "user"
                      ? "bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-br-none"
                      : "bg-white border border-gray-200 text-gray-800 rounded-bl-none"
                  }`}
                >
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                  {msg.role === "assistant" && !msg.feedbackGiven && (
                    <div className="flex flex-col gap-0.5 mt-1.5 text-xs text-gray-400 border-t pt-1">
                      <div className="flex items-center gap-2">
                        <span>Was this helpful?</span>
                        <button
                          onClick={() => handleFeedback(msg.messageId, true)}
                          className="hover:text-teal-600"
                        >
                          <ThumbsUp size={10} />
                        </button>
                        <button
                          onClick={() => handleFeedback(msg.messageId, false)}
                          className="hover:text-red-600"
                        >
                          <ThumbsDown size={10} />
                        </button>
                        {msg.usedFallback && (
                          <a
                            href="https://virtualscribe.ai/help-center"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-blue-600 flex items-center gap-0.5"
                          >
                            <ExternalLink size={9} />
                          </a>
                        )}
                      </div>
                      <button
                        onClick={resetChat}
                        className="text-left text-blue-600 hover:underline flex items-center gap-0.5 mt-0.5"
                      >
                        <Home size={10} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-lg rounded-bl-none px-3 py-1.5 text-xs text-gray-500">
                  <div className="flex items-center gap-0.5">
                    <span className="animate-pulse">●</span>
                    <span className="animate-pulse delay-100">●</span>
                    <span className="animate-pulse delay-200">●</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <ChatInput
        onSend={sendMessage}
        onReset={resetChat}
        isLoading={isLoading}
      />
    </>
  );

  const FullView = () => (
    <div
      className="tour-hide-on-mobile fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
      style={{
        maxHeight: "calc(80vh - 2rem)",
        minHeight: "250px",
      }}
    >
      <Header />
      <ChatBody />
    </div>
  );

  const MinimizedView = () => (
    <div
      className="tour-hide-on-mobile fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
      style={{
        maxHeight: "calc(40vh - 2rem)",
        minHeight: "150px",
      }}
    >
      <Header />
      <ChatBody />
    </div>
  );

  return isMinimized ? <MinimizedView /> : <FullView />;
};

export default ChatBot;